import { useState, useEffect, Fragment, useCallback, useMemo, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import styled from "@mui/system/styled";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import MoveUpOutlinedIcon from "@mui/icons-material/MoveUpOutlined";
import { GetProjectResponse, WorkItemResponse, UpdateWorkItemOrderRequest } from "api/generatedApi";
import { ExtendedProjectFolder } from "pages/project/hooks/use-map-tree-to-flat";
import { DesktopWorkItemMaterialTableRow } from "./desktop-work-item-material-row";
import { sortCompareDateString, sortCompareNumber, sortCompareString } from "utils/compares";
import { getHmsFromMilliSeconds } from "utils/formats";
import { DesktopWorkItemOperationTableRow } from "./desktop-work-item-operation-row";
import { FolderLockedButton } from "components/page/folder/lock-folder/folder-locked-button";
import { TextIcon } from "components/shared/text-icon/text-icon";
import { useWorkItemRestrictions } from "shared/user-restrictions/use-workitems-restrictions";
import { useMoveWorkitems } from "../../move/hooks/use-move";
import { useDeleteWorkitems } from "../../hooks/use-delete-workitem";
import { useCopyWorkItems } from "../../copy/hooks/use-copy";
import { useOrder } from "shared/table/use-order";
import { TableHeaderConfig } from "shared/table/table-header-type";
import { TableHeader } from "shared/table/table-header";
import { MultiplyFolderButton } from "components/page/folder/multiply-folder/multiply-folder-button";
import { useFeatures } from "utils/hooks/use-features";
import { useSortOrderWorkItems } from "../../hooks/use-sort-order-workitems";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';


interface Props {
  project: GetProjectResponse;
  selectedFolder: ExtendedProjectFolder;
  workItems?: WorkItemResponse[];
  dataFlatlist: ExtendedProjectFolder[];
  lastEditedId?: string | null;
  setLastEdited: Dispatch<SetStateAction<string | null>>;
  scrollToRow: (rowRef: React.RefObject<HTMLTableRowElement>, shouldScrollIntoView: boolean) => void;
  sortItemsState: boolean;
  setSortItemState: Dispatch<SetStateAction<boolean>>;
}

export const DesktopFolderWorkItems = (props: Props) => {
  type WorkItemSortableId = "created" | "material" | "moutingCode" | "operationSupplement" | "amount" | "unit" | "operationTime" | "operationTotalTime" | "payment" | "sortOrder";
  const { workItems, project, selectedFolder, dataFlatlist, lastEditedId, setLastEdited, sortItemsState, setSortItemState } = props;
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatures();
  const { canSelectWorkItem, canDeleteWorkitems, canMoveWorkitems, canCopyWorkitems, canReOrderWorkItems } = useWorkItemRestrictions(project);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set<string>());
  const [sortedWorkItems, setSortedWorkItems] = useState<WorkItemResponse[]>(workItems ?? []);
  const [checkedAll, setCheckedAll] = useState(false);
  const moveWorkitemsDialog = useMoveWorkitems({ project, folder: selectedFolder, dataFlatlist, selectedWorkitemIds: selectedIds });
  const copyWorkItemsDialog = useCopyWorkItems({ project, dataFlatlist, selectedFolder, selectedIds });
  const openDeleteMeasurementDialog = useDeleteWorkitems({ project, folder: selectedFolder, selectedIds });
  const { direction, orderBy, getLabelProps } = useOrder<WorkItemSortableId>("sortOrder", "desc");
  const useSortOrder = useSortOrderWorkItems({ project, folder: selectedFolder });



  const onCheck = useCallback(
    (id: string | undefined) => {
      if (id === undefined) return;
      let ids = new Set(selectedIds);
      if (ids.has(id)) {
        ids.delete(id);
      } else {
        ids.add(id);
      }
      setSelectedIds(ids);
    },
    [selectedIds]
  );

  const onCheckAll = (checked: boolean) => {
    let ids = new Set<string>();
    if (checked) {
      workItems?.forEach((item) => ids.add(item.workItemId ?? ""));
    }
    setSelectedIds(ids);
    setCheckedAll(!checkedAll);
  };

  const isWorkItemChecked = (id: string | undefined): boolean => {
    return selectedIds.has(id ?? "");
  };

  useEffect(() => {
    if (workItems && workItems.length > 0) {
      setSortedWorkItems(workItems);
    } else {
      setSortedWorkItems([]);
    }
  }, [workItems]);

  // When entering sort mode, initialize sortedWorkItems with properly sorted array
  useEffect(() => {
    if (sortItemsState && workItems && workItems.length > 0) {
      const sorted = [...workItems].sort((a, b) =>
        sortCompareNumber("desc", a.workItemOrderIndex, b.workItemOrderIndex)
      );
      setSortedWorkItems(sorted);
    }
  }, [sortItemsState]);

  const headerConfig: TableHeaderConfig<WorkItemSortableId>[] = [
    {
      id: "sortOrder",
      title: t("content.measurements.table.sortOrder"),
      sortable: true,
      testid: "measurements-table-header-sorting-sortorder",
      alignment: "left",
      hide: !sortItemsState,
    },
    {
      id: "material",
      title: t("content.measurements.table.workItem"),
      sortable: true,
      testid: "measurements-table-header-sorting-material",
      alignment: "left",
    },
    {
      id: "moutingCode",
      title: t("content.measurements.table.moutingCode"),
      sortable: false,
      testid: "measurements-table-header-sorting-mountingcode",
      alignment: "left",
    },
    {
      id: "operationSupplement",
      title: t("content.measurements.table.operationSupplement"),
      sortable: false,
      testid: "measurements-table-header-sorting-supplements",
      alignment: "left",
    },
    {
      id: "amount",
      title: t("content.measurements.table.amount"),
      sortable: true,
      testid: "measurements-table-header-sorting-amount",
      alignment: "right",
    },
    {
      id: "operationTotalTime",
      title: t("content.measurements.table.operationTotalTime"),
      sortable: true,
      testid: "measurements-table-header-sorting-total-operationtime",
      alignment: "right",
    },
    {
      id: "payment",
      title: t("content.measurements.table.payment"),
      sortable: true,
      testid: "measurements-table-header-sorting-total-payment",
      alignment: "right",
    },
  ];

  const sortedData = useMemo(() => {
    // When in sort mode, use sortedWorkItems as-is (user is manually reordering)
    if (sortItemsState) {
      return sortedWorkItems;
    }

    // When not in sort mode, apply column-based sorting
    var sortedList = sortedWorkItems;

    switch (orderBy) {
      case "created":
        sortedList = [...sortedList].sort((a, b) => sortCompareDateString(direction, a?.createdInteractionResponse?.timestamp, b?.createdInteractionResponse?.timestamp));
        break;
      case "material":
        sortedList = [...sortedList].sort((a, b) => sortCompareString(direction, a?.workItemText, b?.workItemText));
        break;
      case "amount":
        sortedList = [...sortedList].sort((a, b) => sortCompareNumber(direction, a?.workItemAmount, b?.workItemAmount));
        break;
      case "operationTotalTime":
        sortedList = [...sortedList].sort((a, b) => sortCompareString(direction, getHmsFromMilliSeconds(a.workItemTotalOperationTimeMilliseconds), getHmsFromMilliSeconds(b.workItemTotalOperationTimeMilliseconds)));
        break;
      case "payment":
        sortedList = [...sortedList].sort((a, b) => sortCompareNumber(direction, a.workItemTotalPaymentDkr, b.workItemTotalPaymentDkr));
        break;
      case "sortOrder":
        sortedList = [...sortedList].sort((a, b) => sortCompareNumber(direction, a.workItemOrderIndex, b.workItemOrderIndex));
        break;
    }

    return sortedList;
  }, [sortedWorkItems, orderBy, direction, sortItemsState]);


  const changeWorkItemsOrderindex = (workItemID: string, moveDirection: "up" | "down") => {
    setSortedWorkItems((prev) => {
      // Use current list as-is since it's properly maintained when in sort mode
      const list = [...prev];

      const currentIndex = list.findIndex((w) => w.workItemId === workItemID);
      if (currentIndex < 0) return prev;

      const targetIndex = moveDirection === "up" ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= list.length) return prev;

      const next = [...list];
      [next[currentIndex], next[targetIndex]] = [next[targetIndex], next[currentIndex]];

      // Reassign sequential order indexes to match visual order (backend-friendly).
      return next.map((w, index) => ({ ...w, workItemOrderIndex: index }));
    });
  };

  const getWorkItemRow = (workItem: WorkItemResponse) => {
    switch (workItem.workItemType) {
      case "Material":
        return (
          <DesktopWorkItemMaterialTableRow
            setLastEdited={setLastEdited}
            shouldScrollIntoView={workItem.workItemId === lastEditedId}
            scrollToRow={props.scrollToRow}
            key={workItem.workItemId}
            project={project}
            folder={selectedFolder}
            workItem={workItem}
            onCheck={onCheck}
            checked={isWorkItemChecked(workItem.workItemId)}
            sortItemsState={sortItemsState}
            changeWorkItemsOrderindex={changeWorkItemsOrderindex}
          />
        );
      case "Operation":
      case "ProjectSpecificOperation":
        return (
          <DesktopWorkItemOperationTableRow
            setLastEdited={setLastEdited}
            shouldScrollIntoView={workItem.workItemId === lastEditedId}
            scrollToRow={props.scrollToRow}
            key={workItem.workItemId}
            project={project}
            folder={selectedFolder}
            workItem={workItem}
            onCheck={onCheck}
            checked={isWorkItemChecked(workItem.workItemId)}
            sortItemsState={sortItemsState}
            changeWorkItemsOrderindex={changeWorkItemsOrderindex}
          />
        );
    }
  };

  const setSortItemStateFunc = () => {
    if (!canReOrderWorkItems(selectedFolder)) return;
    setSortItemState(sortItemsState ? false : true);
  }

  const cancelSortItems = () => {
    setSortedWorkItems(workItems ?? []);
    setSortItemState(false);
  }

  const UpdateSortItemClick = () => {
    const updateWorkItemOrderRequest: UpdateWorkItemOrderRequest = {
      workItemOrderIdAndIndexList: sortedWorkItems
        .filter((workItem) => !!workItem.workItemId)
        .map((workItem, index) => ({
          workItemId: workItem.workItemId ?? "",
          workItemOrderIndex: index,
        })),
    };

    useSortOrder(updateWorkItemOrderRequest);
    setSortItemState(false);
  }

  const NoWorkItems = styled(Box)`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-style: italic;
  `;

  return (
    <Box sx={{ flex: 1, overflowY: "hidden", display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
      {workItems && sortedWorkItems.length > 0 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2, gap: 2 }}>

            {/*Buttons to display on sort items state*/}
            {/*-----------------------------------------------------------------------------------*/}
            {sortItemsState && <TextIcon translateText="content.measurements.changeOrder.saveButton">
              <IconButton onClick={UpdateSortItemClick} >
                <SaveIcon />
              </IconButton>
            </TextIcon>}
            {sortItemsState && <TextIcon translateText="content.measurements.changeOrder.cancelButton">
              <IconButton onClick={cancelSortItems} >
                <CancelIcon />
              </IconButton>
            </TextIcon>}
            {/*-----------------------------------------------------------------------------------*/}

            {selectedFolder && !sortItemsState && <FolderLockedButton project={project} folder={selectedFolder} showText={true} />}
            {selectedFolder && !sortItemsState && isFeatureEnabled("FolderMultiplication") && !selectedFolder.isRoot && <MultiplyFolderButton project={project} folder={selectedFolder} />}
            {!sortItemsState && isFeatureEnabled("WorkItemSorting") && <TextIcon translateText={sortItemsState ? "content.measurements.changeOrder.iconFinnish" : "content.measurements.changeOrder.iconBegin"}>
              <IconButton disabled={!canReOrderWorkItems(selectedFolder)} onClick={setSortItemStateFunc}>
                <MoveUpOutlinedIcon />
              </IconButton>
            </TextIcon>}
            {!sortItemsState && <TextIcon translateText="common.move">
              <IconButton disabled={!canMoveWorkitems(selectedFolder, selectedIds)} onClick={moveWorkitemsDialog}>
                <DriveFileMoveOutlinedIcon />
              </IconButton>
            </TextIcon>}
            {!sortItemsState && <TextIcon translateText="common.copyTo">
              <IconButton disabled={!canCopyWorkitems(selectedFolder, selectedIds) || sortItemsState} onClick={copyWorkItemsDialog}>
                <ContentCopyOutlinedIcon />
              </IconButton>
            </TextIcon>}

            {!sortItemsState && <TextIcon translateText="common.delete">
              <IconButton data-testid="measurement-table-delete-row" disabled={!canDeleteWorkitems(selectedFolder, selectedIds) || sortItemsState} onClick={openDeleteMeasurementDialog}>
                <DeleteOutlinedIcon />
              </IconButton>
            </TextIcon>}
          </Box>
          <TableContainer sx={{ height: "calc(100vh - 275px)", overflowX: "scroll" }} component={Paper}>
            <Table stickyHeader>
              <TableHeader method={sortItemsState ? undefined : "checkbox"} headerConfig={headerConfig} getLabelProps={sortItemsState ? undefined : getLabelProps} checked={selectedIds.size === workItems.length} checkBoxDisabled={!canSelectWorkItem(selectedFolder)} onCheckAllProps={onCheckAll} />
              <TableBody >
                {sortedData.map((workItem, index) => {
                  return getWorkItemRow(workItem);
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {workItems && sortedWorkItems.length <= 0 && (
        <Fragment>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5, gap: 2 }}>
            {selectedFolder && (
              <>
                <FolderLockedButton project={project} folder={selectedFolder} showText={true} />
                {isFeatureEnabled("FolderMultiplication") && !selectedFolder.isRoot && <MultiplyFolderButton project={project} folder={selectedFolder} />}
              </>
            )}
          </Box>
          <NoWorkItems color={"grey.100"}>{t("content.measurements.noWorkItemsIsRegistred")}</NoWorkItems>
        </Fragment>
      )}
    </Box>
  );
};
